/**
 * Cloud backup operations (Google Drive, iCloud, etc.)
 */

import type { KeyshareBackupData } from '../types'
import { getAvailableCloudProviders } from './cloudStorage'
import { deriveBackupPasswordFromPasskey, encryptKeyshare } from './crypto-utils'

/**
 * Backup keyshare to cloud provider
 * Currently only Google Drive is supported
 */
export async function backupToCloud(data: KeyshareBackupData, providerId?: string, password?: string): Promise<void> {
  // Determine encryption method and get password + credential ID
  const encryptionMethod: 'passkey' | 'password' = password ? 'password' : 'passkey'
  let backupPassword: string
  let credentialId: string | undefined

  if (password) {
    // Use provided password
    backupPassword = password
  } else {
    // Use passkey-derived password and get the credential ID from the actual passkey used
    const result = await deriveBackupPasswordFromPasskey(data.userId).catch(() => {
      throw new Error('Backup requires either custom password or passkey authentication')
    })
    backupPassword = result.password
    credentialId = result.credentialId
    console.log('[backupToCloud] Using credential ID from passkey:', credentialId)
  }

  const encryptedBackup = await encryptKeyshare(data, backupPassword, encryptionMethod, credentialId)

  const timestamp = Date.now()
  const fileName = `embark-keyshare-backup-${data.userId}-${timestamp}.json`
  const fileContent = JSON.stringify(encryptedBackup, null, 2)

  const providers = getAvailableCloudProviders()

  if (providers.length === 0) {
    throw new Error('No cloud storage providers available in this environment')
  }

  // Use specified provider or first available
  const provider = providerId ? providers.find((p) => p.id === providerId) : providers[0]

  if (!provider) {
    throw new Error(providerId ? `Cloud provider '${providerId}' not available` : 'No cloud storage provider available')
  }

  console.info(`[BACKUP] Using ${provider.name} for cloud backup`)

  // Authenticate if not already authenticated
  if (!provider.isAuthenticated()) {
    console.info(`[BACKUP] Authenticating with ${provider.name}`)
    const authenticated = await provider.authenticate()
    if (!authenticated) {
      throw new Error(`Failed to authenticate with ${provider.name}`)
    }
  }

  // Upload file to cloud storage
  console.info(`[BACKUP] Uploading backup to ${provider.name}`)
  const fileId = await provider.upload(fileName, fileContent, true)

  console.info(`[BACKUP] Successfully uploaded backup to ${provider.name}, file ID: ${fileId}`)
}

/**
 * Get list of available cloud providers
 */
export function getCloudProviders(): Array<{
  id: string
  name: string
  available: boolean
}> {
  const providers = getAvailableCloudProviders()

  return providers.map((p) => ({
    id: p.id,
    name: p.name,
    available: p.isAvailable()
  }))
}
